<template>
  <sistema>
    <container :isModal="false" :loading="!stateReady">
      <template v-slot:header>
        <h3>Prestação de contas RNTRC</h3>
      </template>
      <template v-slot:body>
        <form @submit.prevent="submitSearch">
          <entity-selector-cliente
            :idClienteFiltered="filter.idCliente ?? undefined"
            :canEdit="true"
            @confirm-selection="confirmSelectionCliente"
            :parentLoading="stateLoading"
          />
          <fieldset :disabled="stateLoading">
            <div class="p-formgrid p-grid">
              <div class="p-field p-fluid p-col-12 p-md-4">
                <input-with-label
                  label="Competência"
                  v-slot="l"
                  :required="true"
                >
                  <Calendar
                    :id="l.id"
                    v-model="competencia"
                    view="month"
                    dateFormat="mm/yy"
                    :showIcon="true"
                    :required="true"
                  />
                </input-with-label>
              </div>
              <div class="p-field p-fluid p-col-12 p-md-4">
                <input-with-label label="Tipo" v-slot="l">
                  <Dropdown
                    :id="l.id"
                    v-model="filter.flTipoServico"
                    :options="tiposDisponiveis"
                    optionValue="flag"
                    optionLabel="descricao"
                    :showClear="true"
                    :placeholder="'&nbsp;'"
                  />
                </input-with-label>
              </div>
              <div class="p-field p-fluid p-col-12 p-md-4">
                <input-with-label label="Status do item" v-slot="l">
                  <Dropdown
                    :id="l.id"
                    v-model="filter.flStatusItem"
                    :options="statusDisponiveis"
                    optionValue="flag"
                    optionLabel="descricao"
                    :showClear="true"
                    :placeholder="'&nbsp;'"
                  />
                </input-with-label>
              </div>
              <div class="p-col-12">
                <button-group>
                  <Button
                    type="submit"
                    class="p-button-rounded"
                    label="Buscar"
                    :disabled="!competencia"
                  />
                  <Button
                    label="Prestação de contas (xlsx)"
                    icon="pi pi-file-excel"
                    class="p-button-rounded p-button-success"
                    v-title="
                      'Todas as linhas da competência: cliente, solicitação, pedido ANTT, datas, placa, transportador e valor (taxa ANTT)'
                    "
                    :disabled="!competencia"
                    @click="downloadXlsx()"
                  />
                </button-group>
              </div>
            </div>
          </fieldset>
        </form>

        <DataTable
          :value="resumo"
          :loading="stateLoading"
          class="p-datatable-sm"
          responsiveLayout="stack"
          breakpoint="60rem"
          :stripedRows="true"
          rowGroupMode="subheader"
          groupRowsBy="nomeCliente"
        >
          <template #header>
            <div class="search-result-header">
              <span class="search-result-title"
                >Resumo por cliente, tipo e status</span
              >
              <span class="search-result-count" v-if="resumo.length">
                Total: {{ formatMoeda(totalGeral) }}
              </span>
            </div>
          </template>
          <template #empty>
            <div class="p-text-center p-p-3">
              {{
                buscou
                  ? "Nenhum item na competência."
                  : "Informe a competência e busque."
              }}
            </div>
          </template>
          <template #groupheader="{ data }">
            <b>{{ data.idCliente }} - {{ data.nomeCliente }}</b>
          </template>
          <Column header="Tipo">
            <template #body="{ data }">
              {{ getTipoServicoLabel(data.flTipoServico) }}
            </template>
          </Column>
          <Column header="Status">
            <template #body="{ data }">
              <span
                class="aet-badge"
                :class="'aet-badge--' + getStatusItemCor(data.flStatusItem)"
                v-text="getStatusItemLabel(data.flStatusItem)"
              />
            </template>
          </Column>
          <Column field="quantidade" header="Quantidade" style="width: 8rem" />
          <Column header="Valor unitário" style="width: 9rem">
            <template #body="{ data }">
              {{ formatMoeda(valorUnitario(data)) }}
            </template>
          </Column>
          <Column header="Total" style="width: 9rem">
            <template #body="{ data }">
              <b>{{ formatMoeda(data.valorTotal) }}</b>
            </template>
          </Column>
          <template #groupfooter="{ data }">
            <td colspan="5" class="p-text-right">
              Total do cliente:
              <b>{{ formatMoeda(totalCliente(data.idCliente)) }}</b>
            </td>
          </template>
        </DataTable>
      </template>
    </container>
  </sistema>
</template>

<script lang="ts">
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  onMounted,
  Ref
} from "vue";
import EntitySelectorCliente from "../../components/cliente/EntitySelectorCliente.vue";
import { ClientePayload } from "../../modules/business/cliente/clienteBusiness";
import { useRntrcFaturamentoService } from "../../modules/business/rntrc/rntrcFaturamentoBusiness";
import {
  RntrcFaturamentoFilterPayload,
  RntrcFaturamentoResumoPayload,
  STATUS_ITEM_FATURAMENTO_RNTRC,
  TIPOS_SERVICO_RNTRC,
  formatMoeda,
  getStatusItemCor,
  getStatusItemLabel,
  getTipoServicoLabel,
  toCompetencia
} from "../../modules/business/rntrc/rntrcTypes";

interface RntrcPrestacaoContasState {
  stateReady: boolean;
  loadings: Array<Ref<boolean>>;
  competencia: Date | null;
  filter: RntrcFaturamentoFilterPayload;
  resumo: Array<RntrcFaturamentoResumoPayload>;
  buscou: boolean;
}

export default defineComponent({
  setup() {
    const hoje = new Date();
    const state = reactive<RntrcPrestacaoContasState>({
      stateReady: false,
      loadings: [],
      // fechamento é feito no mês seguinte: abre no mês anterior
      competencia: new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1),
      filter: {},
      resumo: [],
      buscou: false
    });

    const stateLoading = computed(() => state.loadings.length !== 0);

    const removeLoading = (loading: Ref<boolean>) => {
      const idx = state.loadings.indexOf(loading);
      if (idx >= 0) {
        state.loadings.splice(idx, 1);
      }
    };

    const filtroAtual = (): RntrcFaturamentoFilterPayload => ({
      ...state.filter,
      competencia: toCompetencia(state.competencia)
    });

    const submitSearch = () => {
      if (!state.competencia) {
        return;
      }
      const { data, loading, resumo } = useRntrcFaturamentoService();
      state.loadings.push(loading);
      resumo(filtroAtual())
        .then(() => {
          const linhas: Array<RntrcFaturamentoResumoPayload> = Array.isArray(
            data.value
          )
            ? data.value
            : [];
          state.resumo = linhas.sort((a, b) =>
            (a.nomeCliente ?? "").localeCompare(b.nomeCliente ?? "")
          );
          state.buscou = true;
          removeLoading(loading);
        })
        .catch(() => removeLoading(loading));
    };

    const downloadXlsx = () => {
      if (!state.competencia) {
        return;
      }
      const { loading, downloadXlsx } = useRntrcFaturamentoService();
      state.loadings.push(loading);
      downloadXlsx(filtroAtual())
        .then(() => removeLoading(loading))
        .catch(() => removeLoading(loading));
    };

    const confirmSelectionCliente = (c?: ClientePayload) => {
      state.filter.idCliente = c && c.idCliente ? c.idCliente : null;
    };

    const valorUnitario = (r: RntrcFaturamentoResumoPayload) =>
      r.quantidade ? (r.valorTotal ?? 0) / r.quantidade : null;

    const totalCliente = (idCliente?: number | null) =>
      state.resumo
        .filter((r) => r.idCliente === idCliente)
        .map((r) => r.valorTotal ?? 0)
        .reduce((a, b) => a + b, 0);

    const totalGeral = computed(() =>
      state.resumo.map((r) => r.valorTotal ?? 0).reduce((a, b) => a + b, 0)
    );

    onMounted(() => {
      state.stateReady = true;
      submitSearch();
    });

    return {
      stateLoading,
      tiposDisponiveis: TIPOS_SERVICO_RNTRC,
      statusDisponiveis: STATUS_ITEM_FATURAMENTO_RNTRC,
      submitSearch,
      downloadXlsx,
      confirmSelectionCliente,
      valorUnitario,
      totalCliente,
      totalGeral,
      formatMoeda,
      getStatusItemCor,
      getStatusItemLabel,
      getTipoServicoLabel,
      ...toRefs(state)
    };
  },
  components: {
    EntitySelectorCliente
  }
});
</script>

<style scoped lang="scss"></style>
